import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';

import { api } from '../../../lib/api.js';
import { TeamResponse, DashboardRole, CreateInvitationRequest } from '@raven/shared-types';

export function TeamPage() {
  const { id } = useParams({ strict: false }) as { id: string };
  const qc = useQueryClient();
  const team = useQuery({
    queryKey: ['team', id],
    queryFn: () => api.get(`/api/workspaces/${encodeURIComponent(id)}/team`, TeamResponse),
  });

  const [email, setEmail] = useState('');
  const [role, setRole] = useState<(typeof DashboardRole)['_type']>('customer_operator');

  const invite = useMutation({
    mutationFn: () =>
      api.post(
        '/api/invitations',
        CreateInvitationRequest.parse({ email, role, workspace_id: id, ttl_hours: 72 }),
      ),
    onSuccess: () => {
      setEmail('');
      qc.invalidateQueries({ queryKey: ['team', id] });
    },
  });

  return (
    <div className="space-y-4">
      <div className="card">
        <h3 className="text-base font-semibold">Invite a teammate</h3>
        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
          <input
            className="input"
            type="email"
            placeholder="name@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <select className="input" value={role} onChange={(e) => setRole(e.target.value as any)}>
            {DashboardRole.options.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
          <button
            className="btn-primary"
            disabled={!email || invite.isPending}
            onClick={() => invite.mutate()}
          >
            {invite.isPending ? 'Sending…' : 'Send invitation'}
          </button>
        </div>
        {invite.error && (
          <p className="mt-2 text-sm text-red-600">{(invite.error as Error).message}</p>
        )}
      </div>
      <div className="card">
        <h3 className="text-base font-semibold">Members</h3>
        <table className="mt-3 min-w-full text-sm">
          <thead>
            <tr className="border-b">
              <th className="text-left py-2">Email</th>
              <th className="text-left py-2">Name</th>
              <th className="text-left py-2">Role</th>
              <th className="text-left py-2">Status</th>
              <th className="text-right py-2">Active sessions</th>
            </tr>
          </thead>
          <tbody>
            {(team.data?.members ?? []).map((m) => (
              <tr key={m.email_id} className="border-b last:border-b-0">
                <td className="py-2 font-mono text-xs">{m.email_id}</td>
                <td className="py-2">{m.name ?? '—'}</td>
                <td className="py-2">{m.role ?? 'customer_operator'}</td>
                <td className="py-2">{m.client_access_status ?? '—'}</td>
                <td className="py-2 text-right">{m.active_sessions ?? 0}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
